import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, XCircle } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { certificatesApi } from "@/lib/api";
import { COMPANY } from "@/lib/company";

export default function VerifyPage() {
    const { slug } = useParams();
    const [cert, setCert] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        certificatesApi
            .verify(slug)
            .then(setCert)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [slug]);

    return (
        <div
            className="min-h-[calc(100vh-72px)] bg-brand-offwhite py-16 md:py-24"
            data-testid="verify-page"
        >
            <div className="container-x">
                <div className="mx-auto max-w-3xl">
                    {loading ? (
                        <p
                            className="font-mono-tag text-brand-steel"
                            data-testid="verify-loading"
                        >
                            VERIFYING…
                        </p>
                    ) : error ? (
                        <div
                            className="border-2 border-destructive bg-white p-10 text-center"
                            data-testid="verify-invalid"
                        >
                            <XCircle className="mx-auto h-14 w-14 text-destructive" />
                            <h1 className="mt-6 font-display text-5xl">
                                UNRECOGNISED CERTIFICATE
                            </h1>
                            <p className="mt-4 font-body text-brand-steel">
                                This verification link does not match any certificate issued by ElevateBuild.
                            </p>
                            <Link
                                to="/"
                                className="mt-8 inline-flex btn-primary"
                                data-testid="verify-back-home"
                            >
                                Back to ElevateBuild
                            </Link>
                        </div>
                    ) : cert ? (
                        <Reveal>
                            <div
                                className="border-4 border-brand-gold bg-white p-6 md:p-10"
                                data-testid="verify-valid"
                            >
                                <div className="border border-brand-charcoal p-8 md:p-10">
                                    <div className="flex items-center gap-3">
                                        <ShieldCheck className="h-8 w-8 text-brand-gold" />
                                        <p className="font-mono-tag text-brand-gold">
                                            VERIFIED · {cert.status}
                                        </p>
                                    </div>
                                    <h1 className="mt-6 font-display text-5xl leading-[1.1] md:text-6xl">
                                        CERTIFICATE VERIFIED.
                                    </h1>
                                    <p className="mt-6 font-body text-lg text-brand-steel">
                                        This certificate was awarded to:
                                    </p>
                                    <p
                                        className="mt-3 font-display text-4xl uppercase md:text-5xl"
                                        data-testid="verify-holder-name"
                                    >
                                        {cert.holder_name}
                                    </p>
                                    <p className="mt-6 font-body text-lg text-brand-steel">
                                        for successful completion of:
                                    </p>
                                    <p
                                        className="mt-2 font-display text-3xl text-brand-gold"
                                        data-testid="verify-course-title"
                                    >
                                        {cert.course_title}
                                    </p>

                                    <div className="mt-10 grid grid-cols-1 gap-6 border-t border-brand-line pt-6 sm:grid-cols-3">
                                        <div>
                                            <p className="font-mono-tag text-brand-steel">
                                                CERTIFICATE NO.
                                            </p>
                                            <p
                                                className="mt-1 font-display text-lg"
                                                data-testid="verify-cert-number"
                                            >
                                                {cert.cert_number}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="font-mono-tag text-brand-steel">
                                                ISSUED
                                            </p>
                                            <p className="mt-1 font-display text-lg">
                                                {new Date(cert.issued_at).toLocaleDateString(
                                                    "en-GB",
                                                    {
                                                        day: "numeric",
                                                        month: "long",
                                                        year: "numeric",
                                                    },
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="font-mono-tag text-brand-steel">
                                                ISSUED BY
                                            </p>
                                            <p className="mt-1 font-display text-lg">
                                                ELEVATEBUILD
                                            </p>
                                            <p className="mt-1 font-mono-tag text-brand-steel">
                                                Co. No. {COMPANY.companyNumber}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <p className="mt-6 text-center font-mono-tag text-brand-steel">
                                    VERIFICATION REFERENCE · {cert.verification_slug}
                                </p>
                            </div>
                        </Reveal>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
